import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	BarrelLayoutRow,
	CartridgeOption,
	RollsetDetail,
	RollsetPreparedRow,
	RollsetSearchResult,
	Stand,
} from "../models/RollsCartridge";

// Diameters in the legacy and on the shop floor are tracked with 3 decimal
// places (e.g. 15.406, 15.781, 17.156). Rounding to 2 lost the third decimal
// and surfaced as 15.41 rendered as "15.410" — looking like a sync bug when
// the data was actually correct. Round to 3 to clean up IEEE 754 noise
// without dropping real precision.
const round3 = (value: number | null | undefined): number | null => {
	if (value === null || value === undefined) return null;
	return parseFloat(Number(value).toFixed(3));
};

export const getRollsetsPrepared = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<RollsetPreparedRow[]> => {
	const apiService = new MESApiService();
	// Every stand, always: the toolbar filters choose which rollset the detail
	// panel opens, they never narrow this list.
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollsCartridgeDataWeb]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as RollsetPreparedRow[];
		return rows.map((row) => ({
			...row,
			TopRollDiameter: round3(row.TopRollDiameter),
			BottomRollDiameter: round3(row.BottomRollDiameter),
			Diameter: round3(row.Diameter),
		}));
	}
	throw new Error(resp.message || "Error fetching prepared rollsets");
};

export const getBarrelLayout = async ({
	rollsetId,
	signal,
}: {
	rollsetId: number;
	signal?: AbortSignal;
}): Promise<BarrelLayoutRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "rollsetId", value: rollsetId }];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollsProduction]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as BarrelLayoutRow[];
		return rows.map((row) => ({
			...row,
			ScheduledTonnage: round3(row.ScheduledTonnage),
			RolledTonnage: round3(row.RolledTonnage),
		}));
	}
	throw new Error(resp.message || "Error fetching barrel layout");
};

export const getRollsetById = async ({
	rollsetId,
	signal,
}: {
	rollsetId: number;
	signal?: AbortSignal;
}): Promise<RollsetDetail | null> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: rollsetId }];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollsets]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as RollsetDetail[];
		const row = rows[0];
		if (!row) return null;
		return {
			...row,
			TopRollDiameter: round3(row.TopRollDiameter),
			BottomRollDiameter: round3(row.BottomRollDiameter),
		};
	}
	throw new Error(resp.message || "Error fetching rollset");
};

const ROLLSET_PICKER_STATUS_CODES = "AVAILABLE,PREPAREDFORLINE,TOASSEMBLE";

export const findRollsets = async ({
	search,
	signal,
}: {
	search: string;
	signal?: AbortSignal;
}): Promise<RollsetSearchResult[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Search", value: search },
		{ name: "StatusCode", value: ROLLSET_PICKER_STATUS_CODES },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[FindRollsets]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollsetSearchResult[];
	}
	throw new Error(resp.message || "Error searching rollsets");
};

export const getCommonStands = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<Stand[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "StandType", value: "COMMON" },
		{ name: "OnlyActives", value: true },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetStands]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Stand[];
	}
	throw new Error(resp.message || "Error fetching stands");
};

/**
 * Cartridge numbers are text that reads as a number ("2", "10", "8B"), so an
 * alphabetical sort puts 10 before 2. Parse the leading digits, keep the ones
 * that have none last, and fall back to the name for ties.
 */
const compareCartridgeNames = (a: string, b: string): number => {
	const aNum = Number.parseInt(a, 10);
	const bNum = Number.parseInt(b, 10);
	const aIsNum = !Number.isNaN(aNum);
	const bIsNum = !Number.isNaN(bNum);

	if (aIsNum && bIsNum && aNum !== bNum) return aNum - bNum;
	if (aIsNum !== bIsNum) return aIsNum ? -1 : 1;
	return a < b ? -1 : a > b ? 1 : 0;
};

export const getCartridgeOptions = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<CartridgeOption[]> => {
	const apiService = new MESApiService();
	// Only the cartridges that actually hold a rollset: the filter picks the
	// rollset to open, so an empty cartridge has nothing to open.
	const parameters: spParameter[] = [
		{ name: "OnlyActives", value: true },
		{ name: "StatusCode", value: "INUSE,PREPAREDFORLINE" },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetCartridges]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as CartridgeOption[];
		return [...rows].sort((a, b) => {
			const aOrder = a.StandOrder ?? Number.MAX_SAFE_INTEGER;
			const bOrder = b.StandOrder ?? Number.MAX_SAFE_INTEGER;
			if (aOrder !== bOrder) return aOrder - bOrder;
			return compareCartridgeNames(a.Name ?? "", b.Name ?? "");
		});
	}
	throw new Error(resp.message || "Error fetching cartridges");
};

/** The cartridges the Prepare dialog can stage a rollset into: the free ones. */
export const getAvailableCartridges = async ({
	millSizeId,
	signal,
}: {
	millSizeId?: number | null;
	signal?: AbortSignal;
} = {}): Promise<CartridgeOption[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	if (millSizeId != null) {
		parameters.push({ name: "MillSizeId", value: millSizeId });
	}

	const resp = await apiService.callV2(
		"[MILL].[GetCartridges]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as CartridgeOption[];
	}
	throw new Error(resp.message || "Error fetching cartridges");
};

export const prepareRollForLine = async ({
	rollsetId,
	standId,
	cartridgeId,
	passPosition,
}: {
	rollsetId: number;
	standId: number;
	cartridgeId: number | null;
	passPosition: number;
}): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "rollsetId", value: rollsetId },
		{ name: "standId", value: standId },
		{ name: "passPosition", value: passPosition },
	];
	if (cartridgeId != null) {
		parameters.push({ name: "cartridgeId", value: cartridgeId });
	}

	const resp = await apiService.callV2(
		"[ROLLS].[PrepareRollForLine]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error preparing rollset for line");
	}
};

export const undoPrepareRollForLine = async ({
	rollsetId,
}: {
	rollsetId: number;
}): Promise<void> => {
	const apiService = new MESApiService();
	// checkValidation stays off, as the proxy that used to sit here forced it:
	// the call has to survive a pre-existing data inconsistency (rollsets listed
	// in RollsetsPreparedForLine whose catRollsets.StatusCode is already
	// 'AVAILABLE'). The procedure defaults it to 1, so it is passed explicitly.
	const parameters: spParameter[] = [
		{ name: "rollsetId", value: rollsetId },
		{ name: "checkValidation", value: false },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[UndoPrepareRollForLine]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error undoing rollset preparation");
	}
};
